import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useParams } from 'react-router';

import { api, unwrap, type Schemas } from '../api/client.js';
import { FullRescan } from '../components/FullRescan.js';
import { GradeRing, GradeSteps } from '../components/GradeRing.js';
import { HelpButton } from '../components/HelpButton.js';
import { HourHeat, weekHeat } from '../components/heat.js';
import { ChangeList, useImprovements, type NewProblem } from '../components/Improvements.js';
import { SetupPanel } from '../components/SetupPanel.js';
import { SiteFacts } from '../components/SiteFacts.js';
import { UptimeChart } from '../components/UptimeChart.js';
import {
  SiteAddress,
  TILE_TONE,
  blindSpot,
  systemLabel,
  useOverview,
  usePlan,
} from '../components/site.js';
import {
  Button,
  LinkButton,
  LoadError,
  Loading,
  Notice,
  StatusBadge,
  UrgencyBadge,
} from '../components/ui.js';
import { errorMessage } from '../errors.js';
import {
  formatDate,
  formatDateTime,
  formatDuration,
  formatNumber,
  formatTime,
  sameDay,
} from '../i18n.js';
import { useMe } from '../session.js';

type Overview = NonNullable<ReturnType<typeof useOverview>['data']>;
type Range = '24h' | '7d' | '30d' | '90d' | '365d';
const RANGES: Range[] = ['24h', '7d', '30d', '90d', '365d'];
const RANGE_DAYS: Record<Range, number> = { '24h': 1, '7d': 7, '30d': 30, '90d': 90, '365d': 365 };
/** Without confirmed ownership a crawl stops at 25 pages (architecture §10.2). */
const UNVERIFIED_CRAWL_PAGES = 25;
const DAY = 86_400_000;

/** Where a tile leads (audit, decision 11): Links and speed to their pages, the rest to its area. */
const tileTarget = (siteId: string, key: string) =>
  key === 'links'
    ? `/sites/${siteId}/links`
    : key === 'performance'
      ? `/sites/${siteId}/speed`
      : `/sites/${siteId}/area/${key}`;

/** Outages of the site; the same request (and cache) as the availability page. */
function useIncidents(siteId: string) {
  return useQuery({
    queryKey: ['site', siteId, 'incidents', 'all'],
    queryFn: () =>
      unwrap(
        api.GET('/sites/{siteId}/incidents', {
          params: { path: { siteId }, query: { status: 'all', limit: 200 } },
        }),
      ),
    // An outage that begins while the page is open shows in the hours and the summary too.
    refetchInterval: 60_000,
  });
}

/**
 * Dashboard of a site (design/pages/app/site-dashboard.html, S3): the state of the site, the grade
 * and what is missing for the next one, the site at a glance, what matters now, the week's changes,
 * the seven areas and availability with response times.
 */
export function SiteDashboard() {
  const { siteId = '' } = useParams();
  const { t } = useTranslation();
  const timeZone = useMe().data?.account.timezone;
  const overview = useOverview(siteId);

  if (overview.isPending) return <Loading />;
  if (overview.isError)
    return (
      <LoadError
        message={errorMessage(overview.error, t)}
        onRetry={() => void overview.refetch()}
      />
    );
  const o = overview.data;
  return (
    <>
      <Head o={o} timeZone={timeZone} />
      <States o={o} timeZone={timeZone} />
      <ScoreCard o={o} timeZone={timeZone} />
      <SiteFacts overview={o} timeZone={timeZone} />
      <TopFindings o={o} />
      <WeekChanges siteId={siteId} timeZone={timeZone} news={o.newThisWeek} />
      <Tiles o={o} />
      <AvailabilityCard siteId={siteId} timeZone={timeZone} />
    </>
  );
}

function Head({ o, timeZone }: { o: Overview; timeZone?: string | undefined }) {
  const { t, i18n } = useTranslation();
  const cms = o.site.cmsDetected
    ? systemLabel(o.site.cmsDetected, o.profile.wordpressVersion)
    : null;
  return (
    <div className="hd">
      <div>
        <div className="title">
          <h1>{o.site.name}</h1>
          <StatusBadge
            state={
              o.site.status === 'paused'
                ? 'paused'
                : o.access.block === 'bot_protection'
                  ? 'setup'
                  : o.availability.state
            }
          />
        </div>
        <div className="sub">
          <SiteAddress url={o.site.url}>{o.site.url}</SiteAddress>
          {cms ? <span>{cms}</span> : null}
          <span>
            {t('site.since', { date: formatDate(o.site.createdAt, i18n.language, timeZone) })}
          </span>
        </div>
      </div>
      <div className="acts">
        <LinkButton to={`/sites/${o.site.id}/report`} variant="secondary" size="sm">
          {t('site.report')}
        </LinkButton>
        <LinkButton to={`/sites/${o.site.id}/help`} variant="primary" size="sm">
          {t('help.cta')}
        </LinkButton>
      </div>
    </div>
  );
}

/** What the customer should know first (site-dashboard.html: down, bot, basic, paused). */
function States({ o, timeZone }: { o: Overview; timeZone?: string | undefined }) {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const client = useQueryClient();
  const plan = usePlan();
  const resume = useMutation({
    mutationFn: () =>
      unwrap(
        api.PATCH('/sites/{siteId}', {
          params: { path: { siteId: o.site.id } },
          body: { status: 'active' },
        }),
      ),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ['site', o.site.id] });
      await client.invalidateQueries({ queryKey: ['sites'] });
    },
  });
  const incident = o.openIncident?.kind === 'downtime' ? o.openIncident : null;
  const unreachable = (location.state as { unreachable?: boolean } | null)?.unreachable;
  const crawlPages = plan
    ? o.site.verified
      ? plan.limits.crawlMaxPages
      : Math.min(UNVERIFIED_CRAWL_PAGES, plan.limits.crawlMaxPages)
    : null;
  const since = (iso: string) =>
    sameDay(new Date(iso), new Date(), timeZone)
      ? t('site.down.since', { time: formatTime(iso, i18n.language, timeZone) })
      : t('site.openIncident', { time: formatDateTime(iso, i18n.language, timeZone) });
  return (
    <>
      {incident ? (
        <Notice
          kind="error"
          title={since(incident.startedAt)}
          action={
            <LinkButton to={`/sites/${o.site.id}/help`} variant="primary" size="sm">
              {t('help.cta')}
            </LinkButton>
          }
        >
          {/* Only what we know: two locations confirmed it (audit, 10.3). */}
          {incident.confidence === 'multi_region' ? t('site.down.confirmed') : null}
        </Notice>
      ) : null}
      <SetupPanel
        siteId={o.site.id}
        access={o.access}
        lastCheckAt={o.availability.lastCheckAt}
        sslDaysLeft={o.security.ssl?.daysLeft ?? null}
        crawlPages={crawlPages}
      />
      {o.site.status === 'paused' ? (
        <Notice
          kind="info"
          title={t('site.paused.title')}
          action={
            <Button
              variant="secondary"
              size="sm"
              busy={resume.isPending}
              onClick={() => resume.mutate()}
            >
              {t('site.paused.resume')}
            </Button>
          }
        >
          {o.site.pauseUntil
            ? `${t('site.paused.until', { date: formatDateTime(o.site.pauseUntil, i18n.language, timeZone) })} `
            : null}
          {t('site.paused.text')}
        </Notice>
      ) : null}
      {resume.isError ? <Notice kind="error">{errorMessage(resume.error, t)}</Notice> : null}
      {unreachable ? <Notice kind="warning">{t('addSite.unreachable')}</Notice> : null}
      {o.availability.lastCheckAt ? null : <Notice kind="info">{t('site.notChecked')}</Notice>}
    </>
  );
}

/** The grade, how it moved, what is missing for the next one, and checking again by hand. */
function ScoreCard({ o, timeZone }: { o: Overview; timeZone?: string | undefined }) {
  const { t, i18n } = useTranslation();
  const titleId = useId();
  const plan = usePlan();
  const h = o.health;
  const trend = h?.trend7d ?? null;
  return (
    <section className="card" aria-labelledby={titleId}>
      <h2 id={titleId} className="sr-only">
        {t('health.title')}
      </h2>
      {/* Variant A (owner 15.10, SP-UX12i): the grade beside checking again, the scale and the
          steps across the card below; on a phone one below the other in this order. */}
      <div className="scale-row">
        {h ? (
          <>
            <div className="ring">
              <GradeRing score={h.score} grade={h.grade} />
              <div>
                <div className="num">
                  {h.score}
                  <small>/ 100</small>
                </div>
                <div className="cap">
                  {t('onboarding.gradeCap', { grade: h.grade, label: h.label })}
                </div>
                {trend !== null ? (
                  <div className={`cap ${trend > 0 ? 'up' : trend < 0 ? 'down' : ''}`}>
                    {trend !== 0 ? <span aria-hidden="true">{trend > 0 ? '▲ ' : '▼ '}</span> : null}
                    {trend === 0
                      ? t('health.trendSame')
                      : t(trend > 0 ? 'health.trendUp' : 'health.trendDown', {
                          points: Math.abs(trend),
                        })}
                    {h.previousGrade
                      ? ` · ${t('health.previousGrade', { grade: h.previousGrade })}`
                      : null}
                  </div>
                ) : null}
              </div>
            </div>
            <GradeSteps grade={h.grade} />
            {h.nextGrade ? (
              <div className="next">
                <b className="h4s">
                  {t('health.stepsTitle', {
                    grade: h.nextGrade.grade,
                    count: h.nextGrade.steps.length,
                  })}
                </b>
                <ol className="steps-list">
                  {h.nextGrade.steps.map((s) => (
                    <li key={s.ruleCode}>
                      <Link
                        to={`/sites/${o.site.id}/findings?open=${encodeURIComponent(s.ruleCode)}`}
                      >
                        {s.title}
                      </Link>
                    </li>
                  ))}
                </ol>
              </div>
            ) : null}
          </>
        ) : (
          <div className="ring">
            <svg viewBox="0 0 100 100" aria-hidden="true">
              <circle
                cx="50"
                cy="50"
                r="42"
                fill="none"
                stroke="var(--line-strong)"
                strokeWidth="6"
              />
            </svg>
            <p className="bodyM">
              {t(o.access.block === 'bot_protection' ? 'health.hidden' : 'health.pending')}
            </p>
          </div>
        )}
        <FullRescan
          siteId={o.site.id}
          panel={{
            lastCheck: o.availability.lastCheckAt
              ? t('rescan.lastCheck', {
                  time: formatDateTime(o.availability.lastCheckAt, i18n.language, timeZone),
                })
              : null,
            limit: plan ? t('rescan.perDay', { count: plan.limits.manualScansPerDay }) : null,
          }}
        />
      </div>
    </section>
  );
}

/** The card without problems when the site is down or hidden: the notice above says more. */
const TOP_BLIND = {
  down: 'site.top.down',
  bot_protection: 'site.top.bot',
  auth_required: 'site.top.auth',
} as const;

/** "Was jetzt wichtig ist" (v2 §4.2): title, priority, places, one sentence, the way on. */
function TopFindings({ o }: { o: Overview }) {
  const { t } = useTranslation();
  const titleId = useId();
  const siteId = o.site.id;
  // Down or out of sight: no problems found is not nothing to do (SP-A2.66).
  const blind = blindSpot(o);
  return (
    <section className="card" aria-labelledby={titleId}>
      <h2 id={titleId}>{t('site.top.title')}</h2>
      {o.topFindings.length === 0 ? (
        blind ? (
          <Notice kind="warning">{t(TOP_BLIND[blind])}</Notice>
        ) : (
          <Notice kind="success">{t('site.top.empty')}</Notice>
        )
      ) : (
        <ol className="top3s">
          {o.topFindings.map((f, i) => (
            <li key={f.id} className="top3">
              <div className="n" aria-hidden="true">
                {i + 1}
              </div>
              <div>
                <div className="row">
                  <UrgencyBadge urgency={f.urgency} />
                  <span className="spec">{t('findings.count', { count: f.places })}</span>
                </div>
                <h3>{f.title}</h3>
                {f.summary ? <p>{f.summary}</p> : null}
                <div className="act">
                  <LinkButton
                    to={`/sites/${siteId}/findings?open=${encodeURIComponent(f.ruleCode)}`}
                    variant="secondary"
                    size="sm"
                    aria-label={`${t('site.top.view')}: ${f.title}`}
                  >
                    {t('site.top.view')}
                  </LinkButton>
                  {f.service ? (
                    <HelpButton
                      siteId={siteId}
                      service={f.service}
                      findingId={f.id}
                      ruleCode={f.ruleCode}
                      size="sm"
                    />
                  ) : null}
                </div>
              </div>
            </li>
          ))}
        </ol>
      )}
      {o.topMore > 0 ? (
        <Link className="more" to={`/sites/${siteId}/findings`}>
          {t('site.top.more', { count: o.topMore })}
        </Link>
      ) : null}
    </section>
  );
}

/**
 * "Was sich diese Woche geändert hat" (T-36/T-40): the good news with what they bring, and the
 * problems that are new this week. Hidden while there is neither.
 */
function WeekChanges({
  siteId,
  timeZone,
  news,
}: {
  siteId: string;
  timeZone?: string | undefined;
  news: readonly NewProblem[];
}) {
  const { t } = useTranslation();
  const titleId = useId();
  const feed = useImprovements(siteId, 7);
  const items = feed.data?.items ?? [];
  if (items.length === 0 && news.length === 0) return null;
  return (
    <section className="card" aria-labelledby={titleId}>
      <h2 id={titleId}>{t('changes.weekChanged')}</h2>
      <ChangeList siteId={siteId} items={items.slice(0, 3)} news={news} timeZone={timeZone} />
      <Link className="more" to={`/sites/${siteId}/changes`}>
        {t('changes.all')}
      </Link>
    </section>
  );
}

/** A tile's area score of the last 30 days (sheet: .tile svg.spark); the badge says the state. */
function Spark({ values }: { values: readonly number[] }) {
  const lo = Math.min(...values);
  const hi = Math.max(...values);
  const y = (v: number) => 32 - ((v - lo) / Math.max(1, hi - lo)) * 28;
  const x = (i: number) => (i * 200) / Math.max(1, values.length - 1);
  return (
    <svg className="spark" viewBox="0 0 200 36" preserveAspectRatio="none" aria-hidden="true">
      <path
        d={values.map((v, i) => `${i === 0 ? 'M' : 'L'}${x(i)} ${y(v)}`).join(' ')}
        fill="none"
        stroke="var(--acc)"
        strokeWidth="1.5"
      />
    </svg>
  );
}

/** The seven areas (site-dashboard.html .tiles): each one a link to where it is explained. */
function Tiles({ o }: { o: Overview }) {
  const { t } = useTranslation();
  const titleId = useId();
  return (
    <section aria-labelledby={titleId}>
      <h2 id={titleId} className="sr-only">
        {t('site.tiles.label')}
      </h2>
      <ul className="tiles">
        {o.tiles.map((tile) => (
          <li key={tile.key}>
            <Link className="tile" to={tileTarget(o.site.id, tile.key)}>
              <div className="t">
                <h3>{t(`site.tiles.${tile.key}`)}</h3>
                <span className={`badge ${TILE_TONE[tile.status]}`}>
                  {t(`tileStatus.${tile.status}`)}
                </span>
              </div>
              {tile.trend.length >= 2 ? <Spark values={tile.trend} /> : null}
              <p className="state">
                {tile.headline}
                {tile.sub ? ` · ${tile.sub}` : null}
              </p>
              {/* What the area is good for (T-35): kept from the code, beneath the state. */}
              <p className="why">{tile.why}</p>
              <span className="go">{t('site.tiles.more')}</span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

type Incident = Schemas['Incident'];

/** Outages of the range: how many and how long together ("1 Ausfall, 46 Minuten"). */
function outagesIn(incidents: readonly Incident[], days: number, now: number) {
  const from = now - days * DAY;
  const hits = incidents.filter(
    (i) => i.kind === 'downtime' && (i.resolvedAt ? Date.parse(i.resolvedAt) : now) > from,
  );
  const seconds = hits.reduce((sum, i) => {
    const start = Math.max(Date.parse(i.startedAt), from);
    const end = i.resolvedAt ? Date.parse(i.resolvedAt) : now;
    return sum + Math.max(0, end - start) / 1000;
  }, 0);
  return { count: hits.length, seconds };
}

/** "Erreichbarkeit und Antwortzeit": the plan's ranges, 7 × 24 hours and the response times. */
function AvailabilityCard({ siteId, timeZone }: { siteId: string; timeZone?: string | undefined }) {
  const { t, i18n } = useTranslation();
  const titleId = useId();
  const [range, setRange] = useState<Range>('24h');
  const uptime = useQuery({
    queryKey: ['site', siteId, 'uptime', range],
    queryFn: () =>
      unwrap(api.GET('/sites/{siteId}/uptime', { params: { path: { siteId }, query: { range } } })),
    refetchInterval: 60_000,
  });
  const week = useQuery({
    queryKey: ['site', siteId, 'uptime', '7d'],
    queryFn: () =>
      unwrap(
        api.GET('/sites/{siteId}/uptime', { params: { path: { siteId }, query: { range: '7d' } } }),
      ),
    refetchInterval: 60_000,
  });
  const incidents = useIncidents(siteId);
  // Ranges the plan shows (v2 §9); the others lead to the plans.
  const allowed: Range[] = uptime.data?.allowedRanges ?? week.data?.allowedRanges ?? ['24h', '7d'];
  // Only once the plan's ranges are known: no offer flashing up on a plan that has them all.
  const known = Boolean(uptime.data ?? week.data);
  const locked = known && RANGES.some((r) => !allowed.includes(r));
  const zone = timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  const now = Date.now();
  const items = incidents.data?.items ?? [];
  const out = outagesIn(items, RANGE_DAYS[range], now);
  const summary =
    uptime.data && uptime.data.uptimePct !== null
      ? [
          t('site.chart.uptime', { value: formatNumber(uptime.data.uptimePct, i18n.language, 2) }),
          incidents.data
            ? out.count === 0
              ? t('site.chart.noOutage')
              : t('site.chart.outages', {
                  count: out.count,
                  duration: formatDuration(out.seconds, i18n.language),
                })
            : null,
        ]
          .filter(Boolean)
          .join(' · ')
      : null;
  return (
    <section className="card chart" aria-labelledby={titleId}>
      <div className="hd">
        <h2 id={titleId}>
          {t('site.chart.title')} {summary ? <small>{summary}</small> : null}
        </h2>
        <div className="ranges" role="group" aria-label={t('site.chart.range')}>
          {RANGES.map((r) => (
            <button
              key={r}
              type="button"
              aria-pressed={range === r}
              className={!known || allowed.includes(r) ? undefined : 'lock'}
              onClick={() => {
                if (allowed.includes(r)) setRange(r);
                else document.getElementById(`${titleId}-plans`)?.focus();
              }}
            >
              {t(`site.chart.ranges.${r}`)}
              {!known || allowed.includes(r) ? null : (
                <span className="sr-only"> ({t('site.chart.lockedShort')})</span>
              )}
            </button>
          ))}
        </div>
      </div>
      {week.data && incidents.data ? (
        <div className="heat-block">
          <b className="h4s">{t('site.chart.heatTitle')}</b>
          <HourHeat rows={weekHeat(week.data.series, items, zone, new Date(now))} />
          <div className="legend">
            <span>{t('site.chart.hour', { hour: 0 })}</span>
            <span>{t('site.chart.hour', { hour: 12 })}</span>
            <span>{t('site.chart.hour', { hour: 23 })}</span>
          </div>
        </div>
      ) : null}
      {uptime.isPending ? <Loading /> : null}
      {uptime.isError ? (
        <LoadError message={errorMessage(uptime.error, t)} onRetry={() => void uptime.refetch()} />
      ) : null}
      {uptime.data ? (
        <UptimeChart
          points={uptime.data.series}
          uptimePct={uptime.data.uptimePct}
          timeZone={timeZone}
          unit={range === '24h' ? 'check' : range === '7d' ? 'hour' : 'day'}
        />
      ) : null}
      {range === '30d' || range === '90d' || range === '365d' ? (
        <p className="note">{t('site.chart.retention')}</p>
      ) : null}
      {locked ? (
        <div className="upsell">
          <span>{t('site.chart.locked')}</span>
          <LinkButton id={`${titleId}-plans`} to="/billing" variant="secondary" size="sm">
            {t('site.chart.lockedCta')}
          </LinkButton>
        </div>
      ) : null}
      <Link className="more" to={`/sites/${siteId}/incidents`}>
        {t('site.incidentsLink')}
      </Link>
    </section>
  );
}
